import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import styled from "styled-components/macro";

import API from "UserProfile/Family/API";

import FnrLoadingBricks from "UI/FnrLoadingBricks";
import FnrNetworkPhoto from "UI/FnrNetworkPhoto";
import {
  FnrPopoverOption,
  FnrPopoverOptionsList,
  FnrPopoverTriggerButton,
  FnrPopoverWrapper,
} from "UI/FnrPopover2";

import useComponentIsMounted from "Utilities/Hooks/useComponentIsMounted";

RelationshipLine.propTypes = {
  relativeUserFname: PropTypes.string.isRequired,
  relativeUserLname: PropTypes.string.isRequired,
  relationshipUserLastName: PropTypes.string.isRequired,
  relativeUserPhotoURL: PropTypes.string,
  readOnly: PropTypes.bool,
  onPaymentMethodsUnshared: PropTypes.func,
};

export default function RelationshipLine({
  relativeUserFname,
  relativeUserLname,
  relativeUserID,
  relativeUserPhotoURL,
  similarRelationships,
  relationshipTypeData,
  relationshipUserLastName,
  mainUserID,
  readOnly,
  onPaymentMethodsUnshared,
}) {
  const [relationshipTypeID, setRelationshipTypeID] = useState(
    relationshipTypeData?.typeID
  );
  const [previousRelationshipTypeID, setPreviousRelationshipTypeID] = useState(
    relationshipTypeData?.typeID
  );

  const [realtionshipName, setRelationshipName] = useState(
    relationshipTypeData?.name || "(unknown)"
  );

  const updateRelationshipTypeID = (typeID) => {
    setRelationshipTypeID(typeID);
  };

  const [saving, setSaving] = useState(false);

  const [showingPopover, setShowingPopover] = useState(false);

  const onOpen = () => setShowingPopover(true);
  const onClose = () => setShowingPopover(false);

  const componentIsMounted = useComponentIsMounted();

  useEffect(() => {
    if (!componentIsMounted) return;

    setRelationshipName(
      similarRelationships.find((rel) => rel.typeID === relationshipTypeID)
        ?.name
    );

    if (relationshipTypeID === previousRelationshipTypeID) return;

    setSaving(true);

    const saveCall = API.saveRelationship({
      profileUserID: mainUserID,
      relationshipUserID: relativeUserID,
      relationshipTypeID,
      saveDirectRelationshipOnly: true,
    });

    saveCall.post().then((resp) => {
      setSaving(false);

      if (resp.paymentMethodsUnshared) {
        onPaymentMethodsUnshared();
      }

      if (resp.conflicts || resp.assignmentErrors) {
        setRelationshipTypeID(previousRelationshipTypeID);
        alert(
          `It looks like this is in conflict with another one of ${relativeUserFname}'s relationships in this household.  Change it first, then you can save this relationship`
        );
      } else setPreviousRelationshipTypeID(relationshipTypeID);
    });

    return () => saveCall.cancel();

    // Aug 2021 → we’re doing a clean slate of all warnings and ain’t got time to look at this so that’s why this is disabled
    // eslint-disable-next-line
  }, [relationshipTypeID]);

  const getName = () => {
    const includeLastName =
      relativeUserLname.toLowerCase() !==
      relationshipUserLastName.toLowerCase();

    return `${relativeUserFname}${
      includeLastName ? ` ${relativeUserLname}` : ""
    }'s`;
  };

  return (
    <Wrapper>
      <FnrNetworkPhoto
        src={relativeUserPhotoURL}
        shape="square"
        thick={1}
        size="sm"
      />
      <Name>{getName()}&nbsp;</Name>

      {readOnly ? (
        <ReadOnlyRelationshipName>{realtionshipName}</ReadOnlyRelationshipName>
      ) : (
        <FnrPopoverWrapper>
          <FnrPopoverTriggerButton disabled={saving}>
            <RelationshipName
              className={
                saving ? "saving" : showingPopover ? "showing_popover" : null
              }
            >
              <div>
                {realtionshipName} <Caret className="far fa-angle-down" />
              </div>
            </RelationshipName>
          </FnrPopoverTriggerButton>
          <FnrPopoverOptionsList
            closeOnSelect
            onOpen={onOpen}
            onClose={onClose}
          >
            {similarRelationships.map((relationshipInfo, index) => {
              return (
                <FnrPopoverOption
                  key={index}
                  selected={relationshipInfo.typeID === relationshipTypeID}
                  onClick={() =>
                    updateRelationshipTypeID(relationshipInfo.typeID)
                  }
                >
                  {relationshipInfo.name}
                </FnrPopoverOption>
              );
            })}
          </FnrPopoverOptionsList>
        </FnrPopoverWrapper>
      )}
      <FnrLoadingBricks show={saving} />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  font-size: 20px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
`;
const Name = styled.div`
  margin-left: 6px;
`;

const RelationshipName = styled.div`
  border-radius: 8px;
  padding: 2px 4px;
  border: 1px solid rgba(130, 140, 141, 0);
  transition: background 0.2s, border 0.2s;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(.showing_popover):not(.saving) {
    border: 1px solid #828c8d;
    background: #fff;
  }

  &:active:not(.saving),
  &.showing_popover {
    border: 1px solid #278dc1;
    background: #fff;
  }

  & > div {
    transition: transform 0.2s;
  }
  &:active > div {
    transform: scale(0.85);
  }
`;
const Caret = styled.div`
  color: #828c8d;
  transition: color 0.2s, opacity 0.2;

  ${RelationshipName}.saving {
    opacity: 0;
  }
  ${RelationshipName}:hover &,
  ${RelationshipName}.showing_popover & {
    color: #278dc1;
  }
`;

const ReadOnlyRelationshipName = styled.div``;
